import { Component, DestroyRef, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CoinLetterTemplate } from '@coin/shared/cms/util';
import { CommunicationStateEnum } from '@coin/shared/communication/util';
import { EditDataTypesEnum } from '@coin/shared/employee-data/util';
import { WorkflowType } from '@coin/shared/core/util-enums';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { HeadcountSettingDropdownItemsType } from '../../enums/headcount-setting-dropdown-items-type.enum';
import { HeadcountSettingsWorkflowTemplateTypeEnum } from '../../enums/headcount-settings-request-state.enum';
import {
  HeadcountApprovalAdminSettingsResponse,
  HeadcountSettingConfig,
  HeadcountSettingContent
} from '../../interfaces/headcount-approval-admin-settings.model';
import { HeadcountApprovalAdminSettingsService } from '../../services/headcount-approval-admin-settings.service';
import { HEADCOUNT_ADMIN_SETTINGS } from './headcount-settings.config';
import { PageHeadlineComponent } from '../page-headline/page-headline.component';
import { NavigationMenuWrapperComponent } from '../navigation-menu-wrapper/navigation-menu-wrapper.component';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  V2CardComponent,
  V2CheckboxComponent,
  V2DropdownComponent,
  DropdownSearchDirective,
  V2NumberInputComponent,
  V2ButtonComponent,
  V2DialogFooterComponent
} from '@coin/shared/core/ui-storybook';
import { TemplateWithValueDirective } from '@coin/shared/core/util-directives';
import { LoadingScreenComponent } from '@coin/shared/core/data-access';

interface Workflow {
  id: string;
  workflowType: string;
  fromLetterState: string;
  toLetterState: string;
  forwardAfterDays: number;
}

@Component({
  selector: 'admin-headcount-settings',
  templateUrl: './headcount-settings.component.html',
  styleUrls: ['./headcount-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    PageHeadlineComponent,
    NavigationMenuWrapperComponent,
    LoadingScreenComponent,
    V2CardComponent,
    MatIcon,
    MatTooltip,
    NgClass,
    NgTemplateOutlet,
    V2CheckboxComponent,
    FormsModule,
    V2DropdownComponent,
    DropdownSearchDirective,
    TemplateWithValueDirective,
    V2NumberInputComponent,
    V2DialogFooterComponent,
    V2ButtonComponent,
    TranslatePipe
  ]
})
export class HeadcountSettingsComponent implements OnInit {
  private readonly toast = inject(ToastrService);
  private readonly headcountService = inject(HeadcountApprovalAdminSettingsService);
  private readonly destroyRef = inject(DestroyRef);

  public managerMails: CoinLetterTemplate[] = [];
  public editDataTypesEnum = EditDataTypesEnum;
  public settingConfig: HeadcountSettingConfig;
  public headcountSettings: HeadcountApprovalAdminSettingsResponse;
  public workflows: Workflow[] = [];
  public emptyValue = null;

  public preNavigateBack(): Observable<string> {
    return of('/');
  }

  ngOnInit(): void {
    this.settingConfig = HEADCOUNT_ADMIN_SETTINGS;
    this.loadWorkflow();
    this.loadSettings();
  }

  public isQuestionnaireForGermanWithPartialEvaluationBoolean(item: HeadcountSettingContent): boolean {
    return item.type === EditDataTypesEnum.BOOLEAN;
  }

  private convertTimespanToDays(timespan: string): number {
    const daysPart = timespan.split(':')[0].split('.')[0];
    return Number.parseInt(daysPart, 10);
  }

  public loadSettings(): void {
    this.headcountService
      .getAdminSettings()
      .pipe(
        tap(data => {
          for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
              const value = data[key];
              if (typeof value === 'string' && /^[0-9]+\.([0-9]{2}:){2}[0-9]{2}$/.test(value)) {
                const days = this.convertTimespanToDays(value);
                data[key] = days;
              }
            }
          }
        }),
        tap(data => {
          this.headcountSettings = data;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  public getDropdownItems(item: HeadcountSettingContent): (string | CoinLetterTemplate)[] {
    switch (item.dropdownType) {
      case HeadcountSettingDropdownItemsType.Mail:
        return this.managerMails;
      default:
        return item.dropdownValues;
    }
  }

  public getDropdownBindedObjectProperty(type: HeadcountSettingDropdownItemsType): string {
    switch (type) {
      case HeadcountSettingDropdownItemsType.Mail:
        return 'id';
      default:
        return null;
    }
  }

  public displayFn(item: CoinLetterTemplate | string): string {
    return typeof item === 'string' ? item : item.title;
  }

  public save(): void {
    for (const key in this.headcountSettings) {
      if (Object.prototype.hasOwnProperty.call(this.headcountSettings, key)) {
        const value = this.headcountSettings[key];
        if (typeof value === 'number') {
          const timespan = this.convertDaysToTimespan(value);
          this.headcountSettings[key] = timespan;
        }
      }
    }
    this.headcountService
      .updateAdminSettings(this.headcountSettings)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadSettings();
        this.toast.success('Updated Settings Successfully.');
      });
  }

  private loadWorkflow(): void {
    this.headcountService
      .getWorkflows(WorkflowType.Standard)
      .pipe(
        map(workflows => workflows.filter(workflow => workflow.type === 'NoActionNeeded').map(workflow => workflow.id)),
        switchMap(workflowIds =>
          forkJoin(
            workflowIds.map(workflowId =>
              this.headcountService.getFilteredWorkflowTemplates(
                HeadcountSettingsWorkflowTemplateTypeEnum.ManagerMail,
                workflowId
              )
            )
          )
        ),
        tap(templates => {
          this.managerMails = templates.flat().filter(item => item?.state === CommunicationStateEnum.Published);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private convertDaysToTimespan(days: number): string {
    return `${days}.00:00:00`;
  }
}
